import { useAuth0 } from '@auth0/auth0-react';
import { configureErrorMessages } from '@portal/rocket-api-client';
import { RouterProvider } from '@tanstack/react-router';
import 'datadog';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { resolveAppMode } from 'utils/appMode';

import { PostMessageProvider } from 'component/postMessage/PostMessageProvider';
import { AppModeProvider, useAppMode } from 'component/root/AppModeProvider';
import ConditionalAuth0Provider from 'component/root/ConditionalAuth0Provider';
import QueryClientProvider, { queryClient } from 'component/root/QueryClientProvider';

import { API_URL, AUTH0_CLIENT_ID, AUTH0_DOMAIN } from './constants';
import { ERROR_MESSAGE_MAP } from './error-message-map';
import { initI18next } from './i18n';
import { createAppRouter } from './router';

const container = document.getElementById('root');
const root = createRoot(container!);

const { isWpSiteControl } = resolveAppMode();

// preventing leaking of the manifest.json
if (!isWpSiteControl) {
  const manifestLink = document.createElement('link');
  manifestLink.setAttribute('rel', 'manifest');
  manifestLink.setAttribute('href', '/portal/manifest.json');

  document.querySelector('head')?.appendChild(manifestLink);
}

async function initializeApp() {
  await initI18next();
  configureErrorMessages(ERROR_MESSAGE_MAP);

  const router = createAppRouter(queryClient);

  function Auth0GatedRouter() {
    const { isLoading } = useAuth0();
    if (isLoading) return null;
    return <RouterProvider router={router} />;
  }

  function InnerApp() {
    const { usesAuth0 } = useAppMode();

    if (!usesAuth0) return <RouterProvider router={router} />;

    return <Auth0GatedRouter />;
  }

  root.render(
    <React.StrictMode>
      <AppModeProvider>
        <ConditionalAuth0Provider
          domain={AUTH0_DOMAIN}
          clientId={AUTH0_CLIENT_ID}
          authorizationParams={{
            redirect_uri: `${window.location.origin}/post-login`,
            audience: API_URL,
          }}
          cacheLocation="localstorage"
          useRefreshTokens
          useRefreshTokensFallback
        >
          <PostMessageProvider>
            <QueryClientProvider>
              <InnerApp />
            </QueryClientProvider>
          </PostMessageProvider>
        </ConditionalAuth0Provider>
      </AppModeProvider>
    </React.StrictMode>
  );
}

initializeApp();
